import { DateHelper } from '@loadsmart/loadsmart-ui'

import { type StopAppointment } from 'core/appointments/domain/Appointment'
import { formatDate, isWithinInterval, parseISO } from 'core/utils/date'

import { type AppointmentFormValues } from '../infra/ShipmentRepository'

export function getAppointmentTimeByType(appointment: StopAppointment) {
  if (appointment.scheduledTime) {
    return {
      type: appointment.scheduledTime.type,
      start: appointment.scheduledTime.start,
      end: appointment.scheduledTime.end,
    }
  }
  if (appointment.suggestedTime) {
    return {
      type: appointment.suggestedTime.type,
      start: appointment.suggestedTime.start,
      end: appointment.suggestedTime.end,
    }
  }
  return {
    type: 'at',
    start: '',
    end: '',
  }
}

export function getAppointmentSlots(appointment: StopAppointment) {
  const date = appointment.scheduledDate || appointment.suggestedDate
  const time = appointment.scheduledTime || appointment.suggestedTime
  return {
    date,
    time,
  }
}

export function getAppointmentDate({
  appointment,
  stopDate,
}: {
  appointment?: StopAppointment
  stopDate?: string | null
}): string {
  if (appointment?.scheduledDate) {
    return DateHelper(appointment.scheduledDate.start).toString()
  }

  if (appointment?.suggestedDate) {
    return DateHelper(appointment.suggestedDate.start).toString()
  }

  if (stopDate) {
    return DateHelper(stopDate).toString()
  }

  return ''
}

export function getInitialValuesFromAppointment(
  initialValues: AppointmentFormValues,
  appointment: StopAppointment
): AppointmentFormValues {
  const { type, start, end } = getAppointmentTimeByType(appointment)
  const date = getAppointmentDate({ appointment })

  return {
    ...initialValues,
    mode: appointment.mode,
    loadingType: appointment.loadingType,
    status: appointment.status,
    date,
    timeType:
      type === 'between'
        ? { label: 'Between', value: 'between' }
        : { label: 'Specific time', value: 'at' },
    time: start,
    timeEnd: type === 'between' ? end : start,
    confirmationNumber: appointment?.confirmationNumber ?? '',
    misschedulingNote: appointment.misschedulingNote ?? '',
  }
}

export function getOpenDockInitialValuesFromAppointment(appointment?: StopAppointment) {
  const { confirmationNumber, misschedulingNote } = appointment || {}
  return {
    confirmationNumber: confirmationNumber || '',
    misschedulingReason: {
      label: '',
      value: '',
    },
    misschedulingNote: misschedulingNote || undefined,
  }
}

export function hasAppointmentReason(
  formValues: AppointmentFormValues,
  appointment?: StopAppointment
) {
  if (!appointment) return false

  if (appointment.status === 'pending') {
    return hasAppointmentChanged(formValues, appointment)
  }

  return appointment.status === 'confirmed'
}

export function hasAppointmentChanged(
  formValues: AppointmentFormValues,
  appointment?: StopAppointment
) {
  if (!appointment || !appointment.suggestedDate || !appointment.suggestedTime) {
    return false
  }

  if (!formValues.date || typeof formValues.date !== 'string' || !formValues.date.trim())
    return false

  const formDate = parseISO(formValues.date)
  const formattedDate = formatDate(formDate, 'yyyy-MM-dd')

  if (formValues.timeType?.value === 'at') {
    return (
      appointment.suggestedDate.start !== formattedDate ||
      Number(formValues.time) < Number(appointment.suggestedTime.start) ||
      Number(formValues.time) > Number(appointment.suggestedTime.end)
    )
  }

  if (appointment.suggestedDate.type === 'between') {
    const formDateWithoutHours = new Date(formDate.setHours(0, 0, 0, 0))

    const isDateWithinRange = isWithinInterval(formDateWithoutHours, {
      start: parseISO(appointment.suggestedDate.start),
      end: parseISO(appointment.suggestedDate.end),
    })

    return !isDateWithinRange
  }

  return (
    appointment.suggestedDate.start !== formattedDate ||
    Number(formValues.time) < Number(appointment.suggestedTime.start) ||
    Number(formValues.timeEnd) > Number(appointment.suggestedTime.end)
  )
}
